import type { PostHog } from "posthog-js";
import { useSyncExternalStore } from "react";

import type { PostHogSetup } from "./posthog";

type PostHogCall = (client: PostHog) => void;

/**
 * posthog-js is about 90 KB gzip, so it loads after the entry instead of with it. Components talk to
 * this facade, which queues calls until the client is ready and drops them when PostHog is disabled
 * or fails to load.
 */
let client: PostHog | undefined;
let pendingCalls: PostHogCall[] | undefined = [];
const flagListeners = new Set<() => void>();

function notifyFlagListeners(): void {
  for (const listener of flagListeners) {
    listener();
  }
}

function whenLoaded(call: PostHogCall): void {
  if (client) {
    call(client);

    return;
  }

  pendingCalls?.push(call);
}

/** Loads and initialises posthog-js. Call once, after cookie consent is known. */
export async function loadPostHog(setup: PostHogSetup | undefined): Promise<void> {
  if (!setup) {
    pendingCalls = undefined;

    return;
  }

  try {
    // oxlint-disable-next-line no-restricted-imports -- this module owns the deferred posthog-js load.
    const { default: posthog } = await import("posthog-js");
    posthog.init(setup.apiKey, setup.options);
    client = posthog;
  } catch (error) {
    pendingCalls = undefined;
    throw error;
  }

  for (const call of pendingCalls ?? []) {
    call(client);
  }
  pendingCalls = undefined;
  client.onFeatureFlags(notifyFlagListeners);
  notifyFlagListeners();
}

const postHog = {
  capture: (...args: Parameters<PostHog["capture"]>): void => {
    whenLoaded((loaded) => {
      loaded.capture(...args);
    });
  },
  identify: (...args: Parameters<PostHog["identify"]>): void => {
    whenLoaded((loaded) => {
      loaded.identify(...args);
    });
  },
  reset: (...args: Parameters<PostHog["reset"]>): void => {
    whenLoaded((loaded) => {
      loaded.reset(...args);
    });
  },
};

export function usePostHog(): typeof postHog {
  return postHog;
}

function subscribeToFlags(listener: () => void): () => void {
  flagListeners.add(listener);

  return () => {
    flagListeners.delete(listener);
  };
}

/** Reads a feature flag and re-renders once PostHog has loaded its flags. Undefined until then. */
export function usePostHogFeatureFlag(key: string): string | boolean | undefined {
  return useSyncExternalStore(subscribeToFlags, () => client?.getFeatureFlag(key));
}
